import { useAuthentication } from '@hooks/authentication';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import * as paymentTemplateApi from '@apis/paymentTemplates';
import { SearchPagenateQuery } from '@models/common';
import {
  DAY_OF_LABEL,
  GetPaymentTemplate,
  GetPublishedPaymentTemplate,
  PaymentTemplate,
  PublishedUser,
  WEEK_OF_LABEL,
} from '@models/paymentTemplate';
import Table, { ColumnsType } from 'antd/es/table';
import { Button, Descriptions, Modal, Popover } from 'antd';
import moment from 'moment';
import { User } from '@models/auth';
import { QuestionOption } from '@models/product';

const PaymentTemplateModal: React.FC<{ paymentTemplate?: PaymentTemplate; onClose: () => void }> = ({
  paymentTemplate,
  onClose,
}) => {
  const PAYMENT_TEMPLATE_INFO_ITEMS = [
    {
      span: 3,
      label: '템플릿명',
      children: paymentTemplate?.name,
    },
    {
      span: 3,
      label: '상품명',
      //children: paymentTemplate?.product?.title || '-',
      chidlren: '-',
    },
    {
      span: 3,
      label: '입력 옵션',
      children: paymentTemplate?.questionOptions
        ?.map((option: QuestionOption, index: number) => `${index + 1}. ${option.question}`)
        .join('\n'),
    },
    {
      span: 3,
      label: '금액',
      children: `${(paymentTemplate?.payment?.amount || 0).toLocaleString()}원`,
    },
    {
      span: 3,
      label: '수업 주기',
      children: paymentTemplate?.schedule?.type === 'weekly' ? '주별 반복' : '월별 반복',
    },
    {
      span: 3,
      label: '수업 반복일자',
      children: (() => {
        const schedule = paymentTemplate?.schedule;
        if (!schedule) {
          return '-';
        }
        if (schedule.type === 'weekly') {
          const weeklyScheduleText = schedule.repeatDayOfWeek
            ?.map(
              (item: { dayOfWeek: keyof typeof DAY_OF_LABEL; time: string }) =>
                `${DAY_OF_LABEL[item.dayOfWeek]} ${item.time}`,
            )
            .join('\n');
          return weeklyScheduleText || '-';
        } else if (schedule.type === 'monthly') {
          const monthlyScheduleText = schedule.repeatDayOfMonth
            ?.map(
              (item: { weekOfMonth: keyof typeof WEEK_OF_LABEL; dayOfWeek: keyof typeof DAY_OF_LABEL; time: string }) =>
                `${WEEK_OF_LABEL[item.weekOfMonth]} ${DAY_OF_LABEL[item.dayOfWeek]} ${item.time}`,
            )
            .join('\n');
          return monthlyScheduleText || '-';
        }
        return '-';
      })(),
    },
    {
      span: 3,
      label: '결제 방식',
      children: paymentTemplate?.payment?.type === 'weekly' ? '주별 결제' : '월별 결제',
    },
    {
      span: 3,
      label: '결제 일자',
      children: (() => {
        const payment = paymentTemplate?.payment;
        if (!payment) {
          return '-';
        }
        if (payment.type === 'weekly') {
          return `매주 ${DAY_OF_LABEL[payment.paymentDayOfWeek]}`;
        } else if (payment.type === 'monthly') {
          return `매월 ${payment.paymentDayOfMonth}일`;
        }
        return '-';
      })(),
    },
    {
      span: 3,
      label: '메모',
      children: paymentTemplate?.memo || '-',
    },
  ];

  return (
    <Modal open={!!paymentTemplate} width={800} onCancel={onClose} footer={null} destroyOnClose>
      <Descriptions
        title="템플릿 정보"
        bordered
        items={PAYMENT_TEMPLATE_INFO_ITEMS.map((item: any) => ({
          ...item,
          children: item.children ?? item.chidlren ?? '-',
        }))}
      />
    </Modal>
  );
};

const PaymentTemplatePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<SearchPagenateQuery>({
    page: 0,
    size: 20,
  });
  const [selectedPaymentTemplate, setSelectedPaymentTemplate] = useState<PaymentTemplate>();
  const { authStore } = useAuthentication();
  const dialog = useOkCancelDialog();

  const publishedPaymentTemplatesQuery = useQuery({
    queryKey: ['published-payment-templates', authStore.user?.id, searchQuery],
    queryFn: () => paymentTemplateApi.searchPublishedPaymentTemplates(searchQuery),
  });
  const cancelPublishedPaymentTemplateMutation = useMutation({
    mutationFn: (paymentTemplateId: string) => paymentTemplateApi.cancelPublishedPaymentTemplate(paymentTemplateId),
    onSuccess: () => {
      publishedPaymentTemplatesQuery.refetch();
      dialog.open({
        type: 'ok',
        title: '결제창 자동발행',
        content: '결제창 자동발행이 취소되었습니다.',
      });
    },
  });

  const TABLE_COLUMNS: ColumnsType<GetPublishedPaymentTemplate> = [
    {
      title: '템플릿',
      dataIndex: 'paymentTemplate',
      render: (paymentTemplate: GetPaymentTemplate) => (
        <Button onClick={() => setSelectedPaymentTemplate(paymentTemplate)}>보기</Button>
      ),
      align: 'center',
    },
    // {
    //   title: '수정',
    //   render: () => {
    //     return <Button onClick={() => {}}>수정</Button>;
    //   },
    //   align: 'center',
    // },
    {
      title: '삭제',
      render: (record) => {
        return (
          <Button
            onClick={() => {
              dialog.open({
                title: '결제창 자동발행 취소',
                content: '정말 취소하시겠습니까?',
                onConfirm: () => {
                  cancelPublishedPaymentTemplateMutation.mutate(record._id);
                },
              });
            }}>
            삭제
          </Button>
        );
      },
      align: 'center',
    },
    {
      title: '파트너명',
      dataIndex: 'partner',
      render: (partner: User) => partner.profile?.nickname || partner.profile?.name || '-',
      align: 'center',
    },
    {
      title: '상품명',
      dataIndex: 'paymentTemplate',
      render: (paymentTemplate: GetPaymentTemplate) => paymentTemplate.product?.title || '-',
      align: 'center',
    },
    {
      title: '발행 대상',
      dataIndex: 'users',
      render: (users: PublishedUser[]) => (
        <Popover content={users.map((user, index) => `${index + 1}. ${user.name} (${user.phone})`).join('\n')}>
          <div>{users.length > 1 ? `"${users[0].name}" 외 ${users.length - 1}명` : users[0].name}</div>
        </Popover>
      ),
      align: 'center',
    },
    {
      title: '발행 기간',
      render: (record) => {
        return `${moment(record.startDate).format('YYYY.MM.DD')} ~ ${moment(record.endDate).format('YYYY.MM.DD')}`;
      },
      align: 'center',
    },
  ];

  return (
    <div className="payment-template">
      <PaymentTemplateModal
        paymentTemplate={selectedPaymentTemplate}
        onClose={() => setSelectedPaymentTemplate(undefined)}
      />
      <div className="page-header">
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div>발행된 결제창 템플릿</div>
        </div>
      </div>
      <div className="page-content">
        <Table
          columns={TABLE_COLUMNS}
          dataSource={publishedPaymentTemplatesQuery.data?.data.contents}
          pagination={{
            current: (publishedPaymentTemplatesQuery.data?.data.page || 0) + 1,
            pageSize: publishedPaymentTemplatesQuery.data?.data.size || 20,
            total: publishedPaymentTemplatesQuery.data?.data.total,
            showSizeChanger: true,
          }}
          loading={publishedPaymentTemplatesQuery.isLoading}
          onChange={(e) => {
            setSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
          }}
        />
      </div>
    </div>
  );
};

export default PaymentTemplatePage;
